import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { modifyMediaUrl } from "../../helpers/modifyMediaUrl";
import { CurrencyText } from "../common/CurrencyText";
import { usePassText } from "../Passes/passI18n";
import { getI18nLanguageFromLocalStorage } from "../../helpers/localStorageHelper";
import { initAndSubscribeShoppingCart, isInCart } from "../../helpers/shoppingCartHelper";
import { useShoppingCartActions } from "../../hooks/useShoppingCartActions";
import { LANGUAGE } from "../../constants/language";
import { translate } from "../../localization/translateService";
import { DATETIME_FORMATS } from "../../constants/datetime-formats";
import { formatDateV2 } from "../../helpers/dateTimeHelper";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";

const CLASS_ITEM_FIELD_KEYS = {
  textbookInfo: "COURSE_MANAGEMENT.FIELD.TEXTBOOK_INFO",
  totalSessions: "COURSE_MANAGEMENT.FIELD.TOTAL_SESSIONS",
  replayAvailable: "COURSE_MANAGEMENT.FIELD.REPLAY_AVAILABLE",
  scheduleInfo: "COURSE_MANAGEMENT.FIELD.SCHEDULE_INFO",
  dateRange: "COURSE_MANAGEMENT.FIELD.DATE_RANGE",
  rating: "CLASS_DETAIL.INFORMATIONS.RATING",
};

const DAY_OF_WEEK_KEYS = [
  "COURSE_MANAGEMENT.LIVE_SESSION_SCHEDULER.WEEKDAYS.SUN",
  "COURSE_MANAGEMENT.LIVE_SESSION_SCHEDULER.WEEKDAYS.MON",
  "COURSE_MANAGEMENT.LIVE_SESSION_SCHEDULER.WEEKDAYS.TUE",
  "COURSE_MANAGEMENT.LIVE_SESSION_SCHEDULER.WEEKDAYS.WED",
  "COURSE_MANAGEMENT.LIVE_SESSION_SCHEDULER.WEEKDAYS.THU",
  "COURSE_MANAGEMENT.LIVE_SESSION_SCHEDULER.WEEKDAYS.FRI",
  "COURSE_MANAGEMENT.LIVE_SESSION_SCHEDULER.WEEKDAYS.SAT",
];

export default function ClassItem({ data, onClick, translations, toast }) {
  const passText = usePassText();
  const classItem = data;
  const videoRef = useRef(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [inCart, setInCart] = useState(!!classItem?.InCart);
  const [shoppingCart, setShoppingCart] = useState(null);
  const deleteLoadingRef = useRef(false);
  const lang = getI18nLanguageFromLocalStorage();
  const hoverTimeoutRef = useRef(null);
  const [fieldLabels, setFieldLabels] = useState({
    textbookInfo: "",
    totalSessions: "",
    replayAvailable: "",
    scheduleInfo: "",
    dateRange: "",
    rating: "",
  });
  const [categoryLabels, setCategoryLabels] = useState([]);
  const [textbookAccordionIndex, setTextbookAccordionIndex] = useState([]);
  const [dayOfWeekLabels, setDayOfWeekLabels] = useState([]);
  const shoppingCartTranslations = {
    ...translations,
    removeItemSuccessLabel: translations?.removeItemSuccess,
  };
  const { addToCart, removeFromCart, confirmRemoveFromCart } = useShoppingCartActions({ toast, translations: shoppingCartTranslations });

  useEffect(() => {
    let unsubscribe = null;
    initAndSubscribeShoppingCart(setShoppingCart).then((unsub) => {
      unsubscribe = unsub;
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!classItem?.publicId) {
      setInCart(false);
      return;
    }
    setInCart(isInCart(shoppingCart, classItem.publicId));
  }, [shoppingCart, classItem?.publicId]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadFieldLabels = async () => {
      try {
        const [textbookInfo, totalSessions, replayAvailable, scheduleInfo, dateRange, rating] = await Promise.all([
          translate(CLASS_ITEM_FIELD_KEYS.textbookInfo, true),
          translate(CLASS_ITEM_FIELD_KEYS.totalSessions, true),
          translate(CLASS_ITEM_FIELD_KEYS.replayAvailable, true),
          translate(CLASS_ITEM_FIELD_KEYS.scheduleInfo, true),
          translate(CLASS_ITEM_FIELD_KEYS.dateRange, true),
          translate(CLASS_ITEM_FIELD_KEYS.rating, true),
        ]);
        setFieldLabels({ textbookInfo, totalSessions, replayAvailable, scheduleInfo, dateRange, rating });
      } catch {}
    };
    loadFieldLabels();
  }, []);

  useEffect(() => {
    const loadDayOfWeekLabels = async () => {
      try {
        const labels = await Promise.all(DAY_OF_WEEK_KEYS.map((key) => translate(key, true)));
        setDayOfWeekLabels(labels);
      } catch {}
    };

    loadDayOfWeekLabels();
  }, []);

  useEffect(() => {
    if (!classItem?.category) {
      setCategoryLabels([]);
      return;
    }

    const categoryKeys = Array.isArray(classItem.category) ? classItem.category : [classItem.category];
    if (categoryKeys.length === 0) {
      setCategoryLabels([]);
      return;
    }

    let cancelled = false;
    const loadCategoryLabels = async () => {
      try {
        const labels = await Promise.all(categoryKeys.map((key) => translate(key, true)));
        if (!cancelled) setCategoryLabels(labels);
      } catch {
        if (!cancelled) setCategoryLabels([]);
      }
    };
    loadCategoryLabels();
    return () => {
      cancelled = true;
    };
  }, [classItem?.category]);

  const getDisplayTitle = () => {
    const fallback = classItem?.title || classItem?.courseName || "";
    if (!classItem) return fallback;

    if (lang === LANGUAGE.KOREAN) {
      return classItem.koreanTitle || classItem.courseTitle || fallback;
    }

    if (lang === LANGUAGE.VIETNAMESE) {
      return classItem.vietnameseTitle || classItem.courseTitle || fallback;
    }

    return fallback;
  };

  const getDisplaySubtitle = () => {
    const fallback = classItem?.description || "";
    if (!classItem) return fallback;

    if (lang === LANGUAGE.KOREAN) {
      return classItem.koreanSubtitle || classItem.description || fallback;
    }

    if (lang === LANGUAGE.VIETNAMESE) {
      return classItem.vietnameseSubtitle || classItem.description || fallback;
    }

    return fallback;
  };

  const displaySubtitle = getDisplaySubtitle();
  const displayTitle = getDisplayTitle();

  const handleRemoveFromCart = async () => {
    setDeleteLoading(true);
    deleteLoadingRef.current = true;
    try {
      const success = await removeFromCart(
        {
          productId: classItem.productPublicId,
          name: displayTitle,
        },
        {
          setLoading: () => {},
          closeDialog: undefined,
        },
      );
      if (success) setInCart(false);
    } finally {
      setDeleteLoading(false);
      deleteLoadingRef.current = false;
    }
  };

  const handleConfirmRemove = (e) => {
    if (e) e.stopPropagation();
    confirmRemoveFromCart(
      {
        name: displayTitle,
        productId: classItem.productPublicId,
      },
      {
        onConfirm: handleRemoveFromCart,
        deleteLoadingRef,
      },
    );
  };

  const handleCartAction = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (deleteLoading) return;
    if (inCart) {
      handleConfirmRemove(e);
    } else {
      const success = await addToCart(classItem.productPublicId, 1, {
        courseTitle: displayTitle,
      });
      if (success) setInCart(true);
    }
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      if (videoRef.current && classItem?.videoUrl) {
        try {
          videoRef.current.src = modifyMediaUrl(classItem.videoUrl);
          videoRef.current.load();
          videoRef.current.currentTime = 0;
          videoRef.current.style.display = "block";
          videoRef.current.classList.add("fade-out");
          videoRef.current
            .play()
            .then(() => {
              videoRef.current.classList.remove("fade-out");
              videoRef.current.classList.add("fade-in");
            })
            .catch(() => {
              videoRef.current.style.display = "none";
            });
        } catch {}
      }
    }, 1000);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.classList.remove("fade-in");
      videoRef.current.classList.add("fade-out");
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.style.display = "none";
          videoRef.current.classList.remove("fade-out", "fade-in");
        }
      }, 300);
    }
  };

  const currencyCode = typeof classItem?.currency === "string" ? classItem.currency : classItem?.currency?.currencyCode;
  const priceLabel = classItem?.isPassCovered
    ? <CurrencyText amount={0} sourceCurrency={currencyCode} />
    : classItem?.price != null ? <CurrencyText amount={classItem.price} sourceCurrency={currencyCode} /> : "";
  const originalPriceLabel = classItem?.originalPrice != null ? <CurrencyText amount={classItem.originalPrice} sourceCurrency={currencyCode} /> : "";
  const startDateLabel = classItem?.startDate ? formatDateV2(classItem.startDate, DATETIME_FORMATS.YYYYdotMMdotDD) : "";
  const endDateLabel = classItem?.endDate ? formatDateV2(classItem.endDate, DATETIME_FORMATS.YYYYdotMMdotDD) : "";

  const startTimeLabel = classItem?.startDate ? formatDateV2(classItem.startDate, DATETIME_FORMATS.HH_mm) : "";
  const endTimeLabel = classItem?.endDate ? formatDateV2(classItem.endDate, DATETIME_FORMATS.HH_mm) : "";

  const openTrainerProfile = (trainerId, e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(`/${lang}/real-time-lectures/${trainerId}`, "_blank");
  };

  if (!classItem) return null;

  return (
    <div className="course-item w-full h-full shadow-1" onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div className="course-item-top aspect-ratio-4-3 relative">
        {!classItem.isFree && !classItem.isBought && (
          <div className={`${inCart ? "in-cart" : "add-to-cart"}`} onClick={handleCartAction}>
            <svg alt="Add to cart" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M16.5 5.25H13.5V4.75C13.5 3.55653 13.0259 2.41193 12.182 1.56802C11.3381 0.724106 10.1935 0.25 9 0.25C7.80653 0.25 6.66193 0.724106 5.81802 1.56802C4.97411 2.41193 4.5 3.55653 4.5 4.75V5.25H1.5C1.16848 5.25 0.850537 5.3817 0.616117 5.61612C0.381696 5.85054 0.25 6.16848 0.25 6.5V15C0.25 15.7293 0.539731 16.4288 1.05546 16.9445C1.57118 17.4603 2.27065 17.75 3 17.75H15C15.7293 17.75 16.4288 17.4603 16.9445 16.9445C17.4603 16.4288 17.75 15.7293 17.75 15V6.5C17.75 6.16848 17.6183 5.85054 17.3839 5.61612C17.1495 5.3817 16.8315 5.25 16.5 5.25ZM6 4.75C6 3.95435 6.31607 3.19129 6.87868 2.62868C7.44129 2.06607 8.20435 1.75 9 1.75C9.79565 1.75 10.5587 2.06607 11.1213 2.62868C11.6839 3.19129 12 3.95435 12 4.75V5.25H6V4.75ZM16.25 15C16.25 15.3315 16.1183 15.6495 15.8839 15.8839C15.6495 16.1183 15.3315 16.25 15 16.25H3C2.66848 16.25 2.35054 16.1183 2.11612 15.8839C1.8817 15.6495 1.75 15.3315 1.75 15V6.75H4.5V9C4.5 9.19891 4.57902 9.38968 4.71967 9.53033C4.86032 9.67098 5.05109 9.75 5.25 9.75C5.44891 9.75 5.63968 9.67098 5.78033 9.53033C5.92098 9.38968 6 9.19891 6 9V6.75H12V9C12 9.19891 12.079 9.38968 12.2197 9.53033C12.3603 9.67098 12.5511 9.75 12.75 9.75C12.9489 9.75 13.1397 9.67098 13.2803 9.53033C13.421 9.38968 13.5 9.19891 13.5 9V6.75H16.25V15Z"
                fill="#3054F1"
              />
            </svg>
          </div>
        )}
        {classItem.thumbnailUrl ? (
          <img className="surface-400" src={modifyMediaUrl(classItem.thumbnailUrl)} alt={classItem.courseName} />
        ) : (
          <div className="no-thumbnail surface-400" />
        )}
        {classItem.videoUrl && <video ref={videoRef} muted loop playsInline className="vod-hover-video" />}
      </div>
      <div className="course-item-details gap-2">
        {classItem?.category && (
          <div className="bg-orange-500 w-fit text-white p-1 px-2 text-xs border-round-2xl">
            <p>{categoryLabels.length > 0 ? categoryLabels[0] : classItem.category}</p>
          </div>
        )}
        <div className="keduall-buttons button-outlined-small-secondary keduall-typography border-primary-color heading-h6-semibold-600 text-primary-default flex justify-content-center">{priceLabel}</div>
        {classItem?.isPassCovered && (
          <div className="dp-badge dp-badge-active w-fit">{passText("DOTORE_PASS.LABEL.COVERED_BY_PASS")}</div>
        )}
        <h6 title={displayTitle} className="keduall-typography heading-h4-semibold-600 two-line-text">
          {displayTitle}
        </h6>

        <p className="keduall-typography text-16-normal-400 two-line-text text-700">{displaySubtitle}</p>

        {/* {classItem.coursePages && classItem.coursePages.length > 0 && (
          <div>
            <Accordion
              multiple
              activeIndex={textbookAccordionIndex}
              onTabChange={(e) => setTextbookAccordionIndex(e.index)}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <AccordionTab header={<p className="keduall-typography text-14-normal-400 pl-2">{fieldLabels.textbookInfo}</p>}>
                <div className="m-0 keduall-typography text-14-normal-400">
                  {classItem.coursePages.map((page, index) => (
                    <div key={index} className="p-2">
                      <p className="keduall-typography text-14-semibold-600 mb-1">{page.title}</p>
                      {page.units.map((unit, unitIndex) => (
                        <p key={unitIndex} className="keduall-typography text-14-normal-400 pl-3 pb-1">
                          {unit.title}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              </AccordionTab>
            </Accordion>
          </div>
        )} */}

        <div className="flex flex-row gap-2 justify-content-between flex-wrap py-2 mt-2">
          <div className="flex align-items-center gap-2">
            <div>
              <i className="pi pi-user bg-orange-500 p-2 border-circle text-white" />
            </div>
            <div>
              <p className="keduall-typography text-14-normal-400">{translations?.INSTRUCTOR}</p>
              {classItem.trainer ? (
                <p className="keduall-typography text-16-semibold-600">
                  <a href={`/${lang}/real-time-lectures/${classItem.trainerId}`} onClick={(e) => openTrainerProfile(classItem.trainerId, e)}>
                    {classItem.trainer}
                  </a>
                </p>
              ) : null}
            </div>
          </div>
          <div>
            <p className="keduall-typography text-14-normal-400">{fieldLabels.rating}</p>

            <h6 className="keduall-typography heading-h6-semibold-600">
              {classItem.rating} <i className="pi pi-star-fill class-filter-rating-star" />
            </h6>
          </div>
        </div>

        <div>
          <p className="keduall-typography text-14-normal-400 my-1">
            {translations?.PROCESS}: {classItem.currentSessionNumber}/{classItem.totalSessionCount}
          </p>
          <ProgressBar
            className="keduall-progress rating-progress-bar w-full"
            value={(classItem.currentSessionNumber / classItem.totalSessionCount) * 100}
            displayValueTemplate={() => <></>}
          ></ProgressBar>
        </div>

        {classItem?.validityPeriodValue && (
          <div className="file -mb-3 mt-3">
            <p>{fieldLabels.replayAvailable}</p>
          </div>
        )}

        <div className="flex flex-row gap-2 flex-wrap mt-4 mb-1">
          {startDateLabel && (
            <div className="flex-1 flex gap-2 p-2 border-round-lg border-1 border-200">
              <div className="flex align-items-start justify-content-center">
                <i className="pi pi-calendar text-orange-400 surface-100 p-2 border-circle" />
              </div>
              <div>
                <p className="keduall-typography text-14-normal-400">{translations?.START_DATE}</p>
                <p className="keduall-typography text-16-normal-400 font-semibold">{startDateLabel}</p>
              </div>
            </div>
          )}
          {endDateLabel && (
            <div className="flex-1 flex gap-2 p-2 border-round-lg border-1 border-200">
              <div className="flex align-items-start justify-content-center">
                <i className="pi pi-calendar text-orange-400 surface-100 p-2 border-circle" />
              </div>
              <div>
                <p className="keduall-typography text-14-normal-400">{translations?.END_DATE}</p>
                <p className="keduall-typography text-16-normal-400 font-semibold">{endDateLabel}</p>
              </div>
            </div>
          )}
        </div>

        {classItem.daysOfWeek && classItem.daysOfWeek.length > 0 && (
          <div>
            <p className="keduall-typography text-14-normal-400">{fieldLabels.scheduleInfo}:</p>
            <div className="flex flex-row gap-2 flex-wrap mt-1">
              {classItem.daysOfWeek.map((day, index) => {
                const dayIndex = typeof day === "number" ? day : Number(day);
                const label = dayIndex >= 0 && dayIndex < dayOfWeekLabels.length ? dayOfWeekLabels[dayIndex] : day;

                return (
                  <div key={`${day}-${index}`} className="bg-orange-500 w-fit text-white p-1 px-2 text-xs border-round-2xl">
                    <p>{label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-row gap-3 justify-content-between flex-wrap">
          <div className="flex align-items-center gap-2 pl-2">
            <i className="pi pi-clock font-semibold" />
            <p className="keduall-typography text-14-normal-400 font-semibold">{startTimeLabel && endTimeLabel ? `${startTimeLabel} ~ ${endTimeLabel}` : ""}</p>
          </div>
          <div className="flex align-items-center gap-2 border-round-lg border-1 border-200 py-1 pl-1 pr-2">
            <div className="flex align-items-center justify-content-center bg-orange-500 w-fit text-white p-1 px-2 border-round-md">
              <i className="pi pi-users" />
            </div>
            <p className="keduall-typography text-14-normal-400">{classItem.enrollmentCount}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
